/**
 * Pooled table-label client (superuser-only).
 *
 * BE: services/plugins/table_control/api/pool_views.py, mounted at /table_control/pool/.
 * Unlike the older `datasets` client there is no download step and no "upload a new
 * version": labels live in Postgres and are written per row, so several people can
 * label the same zone at once. Crops arrive as short-lived presigned S3 URLs.
 */
import { axiosInstance } from './api';

/** Which model head is being labelled. Also gates the funnel (see PooledTableAnnotator). */
export type Stage = 'presence' | 'in_frame' | 'state';
/** Labelled-ness *for the current stage*. */
export type Labelled = 'all' | 'human' | 'ai' | 'unlabelled';
/** Where the CROP came from. A cancelled alert is a hard negative: the model said
 *  "dirty", a human said "no". Only ever combined with `labelled: 'unlabelled'` — it
 *  SPLITS the unlabelled queue rather than being an independent axis. */
/** `report_fix` — кадр случая, который руками исправили в отчёте/Статусе (Алексей 30.10 #3403):
 *  самое острое, размечать первым и по всем классам. */
export type Origin = 'collected' | 'cancelled' | 'report_fix';
/** Ordering for views that HAVE a date — the unlabelled queue has none, so it keeps
 *  the by-table order regardless. */
export type Sort = '' | 'annotated_desc' | 'annotated_asc';

/** The one selector the annotator shows: six mutually-exclusive views, in two rows —
 *  the unlabelled queue split by crop provenance, then the labelled one split by who
 *  labelled it. Crossing the two produced combinations that read as nonsense. */
export type View =
  | 'report_fix' | 'cancelled' | 'collected' | 'unlabelled'
  | 'human' | 'ai' | 'all';

export const VIEW_ROWS: View[][] = [
  ['report_fix', 'cancelled', 'collected', 'unlabelled'],
  ['human', 'ai', 'all'],
];

/** How a view maps onto the two API axes. */
export const viewParams = (v: View): { labelled: Labelled; origin: Origin | '' } => {
  if (v === 'report_fix') return { labelled: 'unlabelled', origin: 'report_fix' };
  if (v === 'cancelled') return { labelled: 'unlabelled', origin: 'cancelled' };
  if (v === 'collected') return { labelled: 'unlabelled', origin: 'collected' };
  return { labelled: v as Labelled, origin: '' };
};

export const STAGES: Stage[] = ['presence', 'in_frame', 'state'];
/** Columns each stage owns — the annotator sends exactly these. */
export const STAGE_FIELDS: Record<Stage, string[]> = {
  presence: ['occupied'],
  in_frame: ['table_in_frame'],
  state: ['native_objects_table', 'personal_items_table',
    'food_dishes_table', 'dirty_dishes_table'],
};

export interface PoolZone {
  zone_id: number;
  zone: string;
  total: number;
  todo_presence: number;
  todo_in_frame: number;
  todo_state: number;
  /** Hard negatives still waiting for a verdict — the highest-value work in the zone. */
  cancelled_todo: number;
}

export interface BatchZone { zone_id: number; zone: string; count: number }

export interface PoolItem {
  id: number;
  zone_id: number;
  table_id: number;
  table: string;
  camera_id: number | null;
  camera: string | null;
  frame_id: number;
  crop_url: string;
  /** null = this head has NO verdict yet. Never render null as false. */
  labels: Record<string, boolean | null>;
  origin: Origin;
  /** Only in batch mode, where the grid spans every zone and a card without its
   *  zone cannot be placed. */
  zone?: string;
  /** Provenance of the requested stage, never the whole crop's last writer. */
  source: 'user' | 'ai' | null;
  annotated_by: string | null;
  annotated_at: string | null;
}

export interface PoolSamples {
  total: number;
  page: number;
  page_size: number;
  stage: Stage;
  labelled: Labelled;
  origin: Origin | '';
  /** Crops this STAGE asks about (after the funnel), vs every live crop in the zone.
   *  Showing both is what stops the funnel-narrowed number reading as missing data. */
  stage_total: number;
  zone_total: number | null;
  facets: {
    labelled: Record<Labelled, number>;
    tables: { table_id: number; table: string; count: number }[];
    /** Splits of the UNLABELLED queue by crop provenance. Like every count here it is
     *  its own total, never narrowed by the current selection. */
    unlabelled_origins: Record<Origin, number>;
    /** Batch mode only: how many crops each zone contributed (a zone with fewer
     *  unlabelled than requested contributes what it has). */
    batch_zones?: BatchZone[];
  };
  items: PoolItem[];
}

/** Per-zone progress for the zone picker (`todo_*` is per stage). */
export const getPoolZones = () =>
  axiosInstance.get<{ zones: PoolZone[]; stages: Stage[] }>('/table_control/pool/zones/');

export const getPoolSamples = (params: {
  collection?: string;
  zone_id: number | null; stage: Stage; labelled: Labelled;
  origin?: Origin | '';
  sort?: Sort;
  /** >0 switches to batch mode: N unlabelled crops from EVERY zone. The batch is the
   *  selection, so zone/chip/table filters do not apply to it. */
  batch?: number;
  table_id?: number | null; page?: number; page_size?: number;
}) =>
  axiosInstance.get<PoolSamples>('/table_control/pool/samples/', {
    params: {
      collection: params.collection || undefined,
      ...(params.batch ? { batch: params.batch } : { zone_id: params.zone_id }),
      stage: params.stage, labelled: params.labelled,
      ...(params.origin && !params.batch ? { origin: params.origin } : {}),
      ...(params.sort ? { sort: params.sort } : {}),
      ...(params.table_id ? { table_id: params.table_id } : {}),
      page: params.page ?? 1, page_size: params.page_size ?? 200,
    },
  });

export type PoolLabelDelta = { id: number } & Record<string, boolean | number>;

/**
 * Write the stage's columns for these rows. The BE fills any head the client omits
 * with `false`, so "reviewed and clean" can never be lost as an empty delta.
 */
export const poolAnnotate = (stage: Stage, items: PoolLabelDelta[]) =>
  axiosInstance.post<{ saved: number; stage: Stage;
    items: Pick<PoolItem, 'id' | 'source' | 'annotated_by' | 'annotated_at'>[] }>(
    '/table_control/pool/annotate/', { stage, items });

/** Soft delete (reversible): the crop leaves training + the queue but the row remains. */
export const poolDelete = (ids: number[], deleted = true) =>
  axiosInstance.post<{ updated: number; deleted: boolean }>(
    '/table_control/pool/delete/', { ids, deleted });

export interface DagsterRunRef {
  run_id: string; status: string; url: string;
  /** true = a run of this job was already going; the BE returned it instead of starting another. */
  already_running?: boolean;
}

export interface TrainStatus {
  labelled_now: number;
  unlabelled: number;
  /** null until the FIRST run trained from a pooled snapshot: earlier runs counted their
   *  baseline off the per-zone datasets, a different scale, so no honest delta exists. */
  new_since_last: number | null;
  last_run: {
    created_at: string;
    trained_up_to: number;
    snapshot_items: number | null;
    snapshot_version_id: string;
    model_version: string;
    dagster_run_url: string;
  } | null;
}

/** What a retrain would train on right now (and how much of it is new). */
export const getTrainStatus = () =>
  axiosInstance.get<TrainStatus>('/table_control/pool/train-status/');

/** Train on what is labelled right now (a human decides the moment, not a counter). */
export const poolRetrain = () =>
  // Dagster's launchRun answers in ~25-60 s — past the global 30 s timeout, which
  // reported a run that was in fact training as "failed" (28.10.2026).
  axiosInstance.post<DagsterRunRef>('/table_control/pool/retrain/', undefined, { timeout: 120000 });

// NOTE: no `poolMergeDeclined` any more. Cancelled alerts are collected straight into
// the pool as unlabelled rows tagged `origin='cancelled'`, so there is nothing to
// transfer — they show up under «Неразмеченные → только отменённые» like any other
// crop waiting for a verdict.

// ─── seeding the pool: collecting UNLABELLED crops for a zone ────────────────
// The zone is chosen in the annotator's own picker, which now lists configured-but-empty
// zones at (0) — so there is no zone list here. These three calls only act on whichever
// zone is already selected; nothing here labels anything.

export interface CollectPrecheck {
  zone_id: number;
  tables: number;
  /** Domain-neutral alias of `tables`, read by the shared «Собрать кропы» dialog so it
   *  needs no per-domain branch. */
  objects: number;
  /** Crops the frame pipeline actually banked in this window — the ceiling on a run.
   *  Shown before launching because it, not the requested size, usually decides the
   *  outcome. */
  available: number;
  pooled: number;
  date_from: string;
  date_to: string;
  tables_list: { table_id: number; name: string }[];
}

export interface CollectRun {
  id: number;
  zone_id: number;
  zone?: string;
  status: 'running' | 'done' | 'failed';
  requested: number;
  available: number;
  collected: number;
  /** Already in the pool, so skipped. An all-skip run means this window is mined out —
   *  widen the dates rather than pressing again. */
  skipped_existing: number;
  failed: number;
  per_table: Record<string, number>;
  error: string;
  launched_by: string;
  date_from: string;
  date_to: string;
  created_at: string;
  finished_at: string | null;
}

/** What a run over this window would find. Cheap; re-fetch whenever the window changes. */
export const getCollectPrecheck = (zone_id: number, days: number) =>
  axiosInstance.get<CollectPrecheck>('/table_control/pool/collect/precheck/', {
    params: { zone_id, days },
  });

/** Queue a collection. Returns the run row immediately — the work happens in celery. */
export const launchCollect = (zone_id: number, target: number, days: number) =>
  axiosInstance.post<CollectRun>('/table_control/pool/collect/', {
    zone_id, target, days,
  });

/** History. Without `zone_id` this is the whole fleet's log of what was collected when. */
export const getCollectRuns = (zone_id?: number, limit = 30) =>
  axiosInstance.get<{ runs: CollectRun[] }>('/table_control/pool/collect/runs/', {
    params: { ...(zone_id ? { zone_id } : {}), limit },
  });
